/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  PropsWithChildren,
} from 'react';
import { useTranslation } from 'react-i18next';

// UI lib components
import {
  Stack,
  Text,
  Button,
  FileButton,
  Progress,
  ActionIcon,
  Group,
  alpha,
  Tooltip,
} from '@mantine/core';

// UI local components
import getFileIcon from '@features/Resource/ResourceIcon/getFileIcon';
import { icon as resourceIconClass } from '@features/Resource/ResourceBadgeSimple/styles/index.css';

// Types
import { ResourceMimeType } from '@app-types/blackroom-app/resourceMimeType';

// Assets
import {
  IconFileUpload as IconUploadFile,
  IconPaperclip as IconAttachResource,
  IconTrash,
  IconX,
  IconInfoCircle,
} from '@tabler/icons-react';

// Hooks
import useQaFileUpload, {
  PendingQaFile,
} from '@features/Support/hooks/useQaFileUpload';
import useResourceClick from '@features/Support/hooks/useResourceClick';

// Types
import { QaResourceInput, ResourceIndexView } from '@app-types/blackroom-api';

// Style
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';
import * as classes from './styles/index.css';

// Re-export the button section style for consumers
export { attachButtonSection } from './styles/index.css';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface UseQaFileUploadListResult {
  /** Current list of uploaded external files */
  externalFiles: QaResourceInput[];
  /** Update the external files list */
  setExternalFiles: React.Dispatch<React.SetStateAction<QaResourceInput[]>>;
  /** Files currently being uploaded */
  pendingFiles: PendingQaFile[];
  /** Whether files are currently being uploaded */
  isUploading: boolean;
  /** Handle file upload from FileButton. Returns the uploaded files. */
  handleFileUpload: (files: File[]) => Promise<QaResourceInput[]>;
  /** Remove a file from the list */
  removeFile: (storagePath: string) => void;
  /** Cancel an ongoing upload */
  cancelUpload: (fileId: string) => void;
  /** Whether there are any files (uploaded or pending) */
  hasFiles: boolean;
}

/* -------------------------------------------------------------------------- */
/*                                  Context                                   */
/* -------------------------------------------------------------------------- */

interface QaFileUploadContextValue {
  externalFiles: QaResourceInput[];
  pendingFiles: PendingQaFile[];
  removeFile: (storagePath: string) => void;
  cancelUpload: (fileId: string) => void;
}

const QaFileUploadContext = createContext<QaFileUploadContextValue | null>(
  null,
);

function useQaFileUploadContext() {
  return useContext(QaFileUploadContext);
}

/* -------------------------------------------------------------------------- */
/*                           Provider Component                               */
/* -------------------------------------------------------------------------- */

export interface QaFileUploadProviderProps extends PropsWithChildren {
  externalFiles: QaResourceInput[];
  pendingFiles: PendingQaFile[];
  removeFile: (storagePath: string) => void;
  cancelUpload: (fileId: string) => void;
}

export function QaFileUploadProvider({
  externalFiles,
  pendingFiles,
  removeFile,
  cancelUpload,
  children,
}: QaFileUploadProviderProps) {
  const value = useMemo(
    () => ({ externalFiles, pendingFiles, removeFile, cancelUpload }),
    [externalFiles, pendingFiles, removeFile, cancelUpload],
  );

  return (
    <QaFileUploadContext.Provider value={value}>
      {children}
    </QaFileUploadContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Custom Hook                                 */
/* -------------------------------------------------------------------------- */

/**
 * Hook that combines useQaFileUpload with state management for external files.
 * Use this hook in parent components and pass the returned values to QaFileList.
 */
export function useQaFileUploadList(): UseQaFileUploadListResult {
  const [externalFiles, setExternalFiles] = useState<QaResourceInput[]>([]);
  const {
    uploadFiles,
    isUploading,
    pendingFiles,
    clearPendingFiles,
    cancelUpload,
  } = useQaFileUpload();

  const handleFileUpload = useCallback(
    async (files: File[]): Promise<QaResourceInput[]> => {
      if (files.length === 0) return [];
      const uploadedFiles = await uploadFiles(files);
      clearPendingFiles();
      if (uploadedFiles.length > 0) {
        setExternalFiles((prev) => [...prev, ...uploadedFiles]);
      }
      return uploadedFiles;
    },
    [uploadFiles, clearPendingFiles],
  );

  const removeFile = useCallback((storagePath: string) => {
    setExternalFiles((prev) =>
      prev.filter((f) => f.storagePath !== storagePath),
    );
  }, []);

  const hasFiles = useMemo(
    () => externalFiles.length > 0 || pendingFiles.length > 0,
    [externalFiles.length, pendingFiles.length],
  );

  return {
    externalFiles,
    setExternalFiles,
    pendingFiles,
    isUploading,
    handleFileUpload,
    removeFile,
    cancelUpload,
    hasFiles,
  };
}

/* -------------------------------------------------------------------------- */
/*                           Upload Button Component                          */
/* -------------------------------------------------------------------------- */

export interface QaFileUploadButtonProps {
  /** Whether files are currently being uploaded */
  isUploading: boolean;
  /** Callback when files are selected */
  onFilesSelected: (files: File[]) => void;
  /** Custom class name for the button section */
  buttonSectionClassName?: string;
}

/**
 * Upload button component. Use with useQaFileUploadList hook.
 */
export function QaFileUploadButton({
  isUploading,
  onFilesSelected,
  buttonSectionClassName = classes.attachButtonSection,
}: QaFileUploadButtonProps) {
  const { t } = useTranslation(['Support']);
  const resetRef = useRef<() => void>(null);

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      onFilesSelected(files ?? []);
      // Reset the file input so the same file can be selected again
      resetRef.current?.();
    },
    [onFilesSelected],
  );

  return (
    <Group gap={4} wrap="nowrap">
      <FileButton
        accept="*"
        onChange={handleFilesSelected}
        multiple
        disabled={isUploading}
        resetRef={resetRef}
      >
        {(props) => (
          <Button
            variant="transparent"
            leftSection={<IconUploadFile size={ICON_SIZE_MAP.small} />}
            classNames={{ section: buttonSectionClassName }}
            p={0}
            loading={isUploading}
            {...props}
          >
            <Text c="blackroomBlack">
              {t(
                'Support:QuestionMenu.replyInThreadForm.fields.externalFile.label',
              )}
            </Text>
          </Button>
        )}
      </FileButton>
      <Tooltip
        label={t(
          'Support:QuestionMenu.replyInThreadForm.fields.externalFile.tooltip',
        )}
        multiline
        w={220}
        zIndex={400}
      >
        <IconInfoCircle size={16} color="gray" style={{ cursor: 'help' }} />
      </Tooltip>
    </Group>
  );
}

/* -------------------------------------------------------------------------- */
/*                        Attach Resource Button Component                    */
/* -------------------------------------------------------------------------- */

export interface AttachResourceButtonProps {
  /** Callback when button is clicked */
  onClick: () => void;
  /** Custom class name for the button section */
  buttonSectionClassName?: string;
}

/**
 * Button to attach internal workspace resources.
 */
export function AttachResourceButton({
  onClick,
  buttonSectionClassName = classes.attachButtonSection,
}: AttachResourceButtonProps) {
  const { t } = useTranslation(['Support']);

  return (
    <Button
      onClick={onClick}
      variant="transparent"
      leftSection={<IconAttachResource size={ICON_SIZE_MAP.small} />}
      classNames={{ section: buttonSectionClassName }}
      p={0}
    >
      <Text c="blackroomBlack">
        {t('Support:QuestionMenu.replyInThreadForm.fields.resource.label')}
      </Text>
    </Button>
  );
}

/* -------------------------------------------------------------------------- */
/*                             File List Component                            */
/* -------------------------------------------------------------------------- */

export interface QaFileListProps {
  /** List of internal workspace resources */
  resources?: ResourceIndexView[];
  /** Callback when a resource is removed */
  onRemoveResource?: (resourceId: string) => void;
  /** List of uploaded files (optional if using QaFileUploadProvider) */
  externalFiles?: QaResourceInput[];
  /** Files currently being uploaded (optional if using QaFileUploadProvider) */
  pendingFiles?: PendingQaFile[];
  /** Callback when an external file is removed (optional if using QaFileUploadProvider) */
  onRemove?: (storagePath: string) => void;
  /** Gap between items (default: 2) */
  gap?: number;
}

/**
 * Component that displays workspace resources, uploaded files, and pending uploads.
 * Can be used standalone with props or wrapped in QaFileUploadProvider.
 */
export function QaFileList({
  resources,
  onRemoveResource,
  externalFiles: externalFilesProp,
  pendingFiles: pendingFilesProp,
  onRemove,
  gap = 2,
}: QaFileListProps) {
  const openResource = useResourceClick();
  const context = useQaFileUploadContext();

  // Use props if provided, otherwise fall back to context
  const externalFiles = externalFilesProp ?? context?.externalFiles ?? [];
  const pendingFiles = pendingFilesProp ?? context?.pendingFiles ?? [];
  const removeFile = onRemove ?? context?.removeFile;
  const cancelUpload = context?.cancelUpload;

  const hasContent =
    (resources && resources.length > 0) ||
    externalFiles.length > 0 ||
    pendingFiles.length > 0;

  if (!hasContent) return null;

  return (
    <Stack gap={gap}>
      {resources?.map((resource) => {
        const FileIcon = getFileIcon(resource.mimeType as ResourceMimeType);
        return (
          <Group key={resource.id} wrap="nowrap" gap={10}>
            <FileIcon.Component
              color={FileIcon.color}
              className={resourceIconClass}
              style={{ backgroundColor: alpha(FileIcon.color, 0.2) }}
            />
            <Text
              fz={12}
              c="blackroomBlack"
              style={{ cursor: 'pointer', flex: 1 }}
              onClick={() => openResource(resource.id)}
            >
              {resource.name}
            </Text>
            {onRemoveResource && (
              <ActionIcon
                variant="subtle"
                color="blackroomGrey"
                onClick={() => onRemoveResource(resource.id)}
              >
                <IconTrash size={15} />
              </ActionIcon>
            )}
          </Group>
        );
      })}
      {externalFiles.map((file) => {
        const FileIcon = getFileIcon(file.mimeType as ResourceMimeType);
        return (
          <Group key={file.storagePath} wrap="nowrap" gap={10}>
            <FileIcon.Component
              color={FileIcon.color}
              className={resourceIconClass}
              style={{ backgroundColor: alpha(FileIcon.color, 0.2) }}
            />
            <Text fz={12} c="blackroomBlack" style={{ flex: 1 }}>
              {file.name}
            </Text>
            {removeFile && (
              <ActionIcon
                variant="subtle"
                color="blackroomGrey"
                onClick={() => removeFile(file.storagePath)}
              >
                <IconTrash size={15} />
              </ActionIcon>
            )}
          </Group>
        );
      })}
      {pendingFiles.map((file) => {
        const PendingFileIcon = getFileIcon(file.file.type as ResourceMimeType);
        return (
          <Group key={file.id} wrap="nowrap" gap={10}>
            <PendingFileIcon.Component
              color={PendingFileIcon.color}
              className={resourceIconClass}
              style={{
                backgroundColor: alpha(PendingFileIcon.color, 0.2),
                opacity: 0.6,
              }}
            />
            <Text fz={12} c="dimmed" style={{ flex: 1 }}>
              {file.name}
            </Text>
            <Progress
              value={file.progress}
              size="sm"
              w={100}
              color="blackroomBlue"
            />
            {cancelUpload && (
              <ActionIcon
                variant="subtle"
                color="blackroomGrey"
                onClick={() => cancelUpload(file.id)}
              >
                <IconX size={15} />
              </ActionIcon>
            )}
          </Group>
        );
      })}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */
/*                        Attach Buttons Group Component                      */
/* -------------------------------------------------------------------------- */

export interface AttachButtonsGroupProps {
  /** Callback when attach resource button is clicked */
  onAttachResource: () => void;
  /** Whether files are currently being uploaded */
  isUploading: boolean;
  /** Callback when files are selected for upload */
  onFilesSelected: (files: File[]) => void;
}

/**
 * Group of attach resource and upload file buttons.
 */
export function AttachButtonsGroup({
  onAttachResource,
  isUploading,
  onFilesSelected,
}: AttachButtonsGroupProps) {
  return (
    <Group gap="md">
      <AttachResourceButton
        onClick={onAttachResource}
        buttonSectionClassName={classes.attachButtonSection}
      />
      <QaFileUploadButton
        isUploading={isUploading}
        onFilesSelected={onFilesSelected}
        buttonSectionClassName={classes.attachButtonSection}
      />
    </Group>
  );
}

/* -------------------------------------------------------------------------- */
/*                      QaFileAttachments Component                           */
/* -------------------------------------------------------------------------- */

export interface QaFileAttachmentsState {
  /** Current list of uploaded external files */
  externalFiles: QaResourceInput[];
  /** Whether files are currently being uploaded */
  isUploading: boolean;
  /** Whether there are any files (uploaded or pending) */
  hasFiles: boolean;
}

export interface QaFileAttachmentsHandle {
  /** Reset the component state (clear all uploaded files) */
  reset: () => void;
}

export interface QaFileAttachmentsProps {
  /** Callback when attach resource button is clicked */
  onAttachResource: () => void;
  /** List of internal workspace resources */
  resources?: ResourceIndexView[];
  /** Callback when a resource is removed */
  onRemoveResource?: (resourceId: string) => void;
  /** Callback when file state changes - use this to get files for form submission. IMPORTANT: Must be memoized with useCallback to avoid infinite loops. */
  onStateChange?: (state: QaFileAttachmentsState) => void;
  /** Callback when files are uploaded - useful for auto-save scenarios */
  onFilesUploaded?: (files: QaResourceInput[]) => void;
  /** Custom class name for the button section */
  buttonSectionClassName?: string;
  /** Layout direction for attach buttons (default: 'horizontal') */
  layout?: 'horizontal' | 'vertical';
  /** Ref to access component methods like reset() */
  ref?: React.Ref<QaFileAttachmentsHandle>;
}

/**
 * Complete file attachments component that handles both dataroom attachments and file uploads.
 * Encapsulates the useQaFileUploadList hook, buttons, and file list display.
 */
export const QaFileAttachments = forwardRef<
  QaFileAttachmentsHandle,
  Omit<QaFileAttachmentsProps, 'ref'>
>(function QaFileAttachments(
  {
    onAttachResource,
    resources,
    onRemoveResource,
    onStateChange,
    onFilesUploaded,
    buttonSectionClassName = classes.attachButtonSection,
    layout = 'horizontal',
  },
  ref,
) {
  const {
    externalFiles,
    setExternalFiles,
    pendingFiles,
    isUploading,
    handleFileUpload,
    removeFile,
    cancelUpload,
    hasFiles,
  } = useQaFileUploadList();

  // Expose reset function to parent via ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      setExternalFiles([]);
    },
  }));

  // Notify parent of state changes
  useEffect(() => {
    onStateChange?.({ externalFiles, isUploading, hasFiles });
  }, [externalFiles, isUploading, hasFiles, onStateChange]);

  // Wrap handleFileUpload to call onFilesUploaded after upload
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      const uploadedFiles = await handleFileUpload(files);
      if (uploadedFiles.length > 0) {
        onFilesUploaded?.(uploadedFiles);
      }
    },
    [handleFileUpload, onFilesUploaded],
  );

  const hasContent = hasFiles || (resources && resources.length > 0);

  const ButtonsContainer = layout === 'vertical' ? Stack : Group;
  const buttonsContainerProps =
    layout === 'vertical' ? { gap: 4 } : { gap: 'md' };

  return (
    <Stack gap={0} align="flex-start">
      <ButtonsContainer {...buttonsContainerProps} mb={hasContent ? 'sm' : 0}>
        <AttachResourceButton
          onClick={onAttachResource}
          buttonSectionClassName={buttonSectionClassName}
        />
        <QaFileUploadButton
          isUploading={isUploading}
          onFilesSelected={handleFilesSelected}
          buttonSectionClassName={buttonSectionClassName}
        />
      </ButtonsContainer>
      {hasContent && (
        <QaFileUploadProvider
          externalFiles={externalFiles}
          pendingFiles={pendingFiles}
          removeFile={removeFile}
          cancelUpload={cancelUpload}
        >
          <QaFileList
            resources={resources}
            onRemoveResource={onRemoveResource}
          />
        </QaFileUploadProvider>
      )}
    </Stack>
  );
});
